import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  Accordion,
  Typography,
  ToggleButton,
  Radio,
  LoadingHavaianas,
  SlimButton,
  ItemActionTabsType,
  ActionTabs,
  SelectMenu,
  Icon
} from 'components'
import { useSelector, useDispatch } from 'store/store'
import { numberToDecimalString as toDecimal } from 'commons/utils/number'
import {
  getGeneralMeta,
  getGeneralMetaReport,
  getMetaOptions,
  getRanking,
  getRankingReport,
  resetRankingPagination,
  setRankingQuota
} from 'store/HomeContext/homeSlice'
import { OfficeGroup } from 'services/home/interfaces'
import { Skeleton } from '@mui/material'
import { useTakeScreenshot } from 'commons/customHooks'
import { officeColors } from './constants'
import RankingTable from '../RankingTable'
import GeneralTable from '../GeneralTable'

import * as S from './styles'
import { formatDate } from 'commons/utils/date'
import { Offices } from 'constants/enums'

export type OfficeTypes = 'TODOS' | Offices.AC60 | Offices.AESP | Offices.ASMD
export type GoalViews = 'Ranking' | 'General'

export const GoalProgress = () => {
  const dispatch = useDispatch()
  const {
    loadingRanking: loading,
    rankingData,
    generalMetaData,
    rankingTotalVolume,
    rankingQuotaOptions,
    rankingOffices,
    rankingQuota,
    officeGroups,
    rankingUpdateDate,
    loadingRankingReport
  } = useSelector((state) => state.home)
  const [selectedOffice, setSelectedOffice] = useState<OfficeTypes>('TODOS')
  const [selectedView, setSelectedView] = useState<GoalViews>('Ranking')
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
  const [currentTab, setCurrentTab] = useState('0')
  const [isPrinting, setIsPrinting] = useState(false)

  const screenshotComponentRef = useRef(null)
  const takeScreenshot = useTakeScreenshot()

  const handleTakeScreenshot = useCallback(() => {
    setIsPrinting(true)
    setTimeout(() => {
      takeScreenshot(screenshotComponentRef.current)
      setIsPrinting(false)
    }, 500)
  }, [])

  const handleRankingReport = () => {
    if (selectedView === 'Ranking') {
      dispatch(
        getRankingReport({
          AnoMesCota: quota,
          Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
          Regional: selectedGroup,
          Gerente: null
        })
      )
    }
    if (selectedView === 'General') {
      dispatch(
        getGeneralMetaReport({
          AnoMesCota: quota,
          Escritorio: selectedOffice,
          Equipe: null
        })
      )
    }
  }

  const quota = useMemo(() => formatDate(rankingQuota, 'YYYYMM', 'MM/YY'), [rankingQuota])

  const disableReport = useMemo(() => {
    const emptyRanking = selectedView === 'Ranking' && !rankingData.length
    const emptyGeneral = selectedView === 'General' && !generalMetaData.length
    return loading || loadingRankingReport || emptyRanking || emptyGeneral
  }, [loading, loadingRankingReport, rankingData, generalMetaData, selectedView])

  const rankingOfficesTitles: Record<OfficeTypes, string> = {
    TODOS: 'Todos',
    [Offices.AC60]: 'Canal Alimentar (farma e outros)',
    [Offices.AESP]: 'Canal Especializado',
    [Offices.ASMD]: 'AS Moderno'
  }

  const getOfficeTitle = () => {
    return (
      <S.Row>
        <S.OfficeTitle design="hav-default-02-text">
          Escritório {selectedOffice}
        </S.OfficeTitle>
        <S.TitleDivider />
        {selectedOffice !== 'TODOS' && (
          <S.OfficeTitle
            design="hav-default-02-text"
            color={
              [Offices.AC60, Offices.AESP, Offices.ASMD].includes(selectedOffice)
                ? officeColors[selectedOffice]
                : officeColors['TODOS']
            }
          >
            {rankingOfficesTitles[selectedOffice]}
          </S.OfficeTitle>
        )}
      </S.Row>
    )
  }

  const getUpdateDate = () => {
    const day = formatDate(rankingUpdateDate, 'DD/MM', 'DD/MM/YYYY HH:mm:ss')
    const time = formatDate(rankingUpdateDate, 'HH[h]mm', 'DD/MM/YYYY HH:mm:ss')
    return `Dados atualizados em ${day} às ${time}`
  }

  const handleOfficeChange = (office: OfficeTypes) => {
    setSelectedOffice(office)
    dispatch(resetRankingPagination())
    setSelectedGroup(null)
    setCurrentTab('0')
    if (selectedView === 'Ranking') {
      dispatch(
        getRanking(
          {
            AnoMesCota: quota,
            Escritorio: office === 'TODOS' ? null : office,
            Regional: null,
            Gerente: null
          },
          { paginas: 0, linhas: 10 }
        )
      )
    } else {
      dispatch(
        getGeneralMeta({
          AnoMesCota: quota,
          Escritorio: office,
          Equipe: null
        })
      )
    }
  }

  const handleTabTitle = (title: string) => {
    if (selectedOffice === Offices.AESP) return title.replace('HAVA ', '')
    return title.replace('Havaianas ', '')
  }

  const getTabItems = () => {
    const items: ItemActionTabsType[] = [
      {
        label: 'Todas',
        onClick: () => handleTabChange(0)
      }
    ]
    officeGroups.forEach((group, index) => {
      items.push({
        label: handleTabTitle(group.Descricao),
        onClick: () => handleTabChange(index + 1, group)
      })
    })
    return items
  }

  const handleTabChange = (index: number, group?: OfficeGroup) => {
    setCurrentTab(`${index}`)
    dispatch(resetRankingPagination())
    setSelectedGroup(group ? group.Codigo : null)
    dispatch(
      getRanking(
        {
          AnoMesCota: quota,
          Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
          Regional: group ? group.Codigo : null,
          Gerente: null
        },
        { paginas: 0, linhas: 10 }
      )
    )
  }

  const handleViewChange = (view: GoalViews) => {
    if (view === 'Ranking') {
      dispatch(resetRankingPagination())
      setSelectedGroup(null)
      setCurrentTab('0')
      dispatch(
        getRanking(
          {
            AnoMesCota: quota,
            Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
            Regional: null,
            Gerente: null
          },
          { paginas: 0, linhas: 10 }
        )
      )
    }
    if (view === 'General') {
      const office =
        selectedOffice === 'TODOS'
          ? (rankingOffices[0].cd_escr_vend as OfficeTypes)
          : selectedOffice
      setSelectedOffice(office)
      dispatch(
        getGeneralMeta({
          AnoMesCota: quota,
          Escritorio: office,
          Equipe: null
        })
      )
    }
    setSelectedView(view)
  }

  const handleQuotaSelect = (value: string) => {
    dispatch(resetRankingPagination())
    dispatch(setRankingQuota(value))
    if (selectedView === 'Ranking') {
      dispatch(
        getRanking(
          {
            AnoMesCota: formatDate(value, 'YYYYMM', 'MM/YY'),
            Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
            Regional: selectedGroup,
            Gerente: null
          },
          { paginas: 0, linhas: 10 }
        )
      )
    }
    if (selectedView === 'General') {
      dispatch(
        getGeneralMeta({
          AnoMesCota: formatDate(value, 'YYYYMM', 'MM/YY'),
          Escritorio: selectedOffice,
          Equipe: null
        })
      )
    }
  }

  useEffect(() => {
    dispatch(getMetaOptions())
  }, [])

  return (
    <S.Wrapper>
      <Accordion
        summaryContent={
          <Typography design="hav-h5-text">
            Progresso de metas: Equipes de vendas
          </Typography>
        }
      >
        <S.Content>
          <S.Row>
            {selectedView === 'Ranking' && (
              <ToggleButton
                content="Todos"
                active={selectedOffice === 'TODOS'}
                onClick={() => handleOfficeChange('TODOS')}
                disabled={loading || loadingRankingReport}
              />
            )}
            {rankingOffices.map((office) => (
              <ToggleButton
                key={office.cd_escr_vend}
                content={`Escritório ${office.cd_escr_vend}`}
                active={selectedOffice === office.cd_escr_vend}
                onClick={() => handleOfficeChange(office.cd_escr_vend as OfficeTypes)}
                disabled={loading || loadingRankingReport}
              />
            ))}
          </S.Row>
          <S.SectionCard>
            <S.TitleRow>
              <S.Row>
                {getOfficeTitle()}
                <S.OfficeTooltip>
                  {loading ? (
                    <Skeleton width={200} height={17} />
                  ) : (
                    <Typography design="hav-h8-text" className="office-info">
                      {getUpdateDate()}
                    </Typography>
                  )}
                </S.OfficeTooltip>
              </S.Row>
              <S.ReportRow>
                <SlimButton
                  content="Relatório"
                  icon={<Icon icon="download" size="15" />}
                  onClick={handleRankingReport}
                  variant={'primary'}
                  loading={loadingRankingReport}
                  disabled={disableReport}
                />
                <SlimButton
                  content="Imagem"
                  icon={<Icon icon="download" size="15" />}
                  onClick={handleTakeScreenshot}
                  variant={'primary'}
                  disabled={disableReport}
                />
              </S.ReportRow>
            </S.TitleRow>
            <S.RowDivider />
            <S.TitleRow>
              <S.RadioGroup>
                <SelectMenu
                  label="Mês cota"
                  options={rankingQuotaOptions}
                  value={rankingQuota}
                  onChange={handleQuotaSelect}
                  disabled={loading || loadingRankingReport}
                  hasBorder
                />
                <Radio
                  label="Ranking"
                  name="goal-view"
                  value="ranking"
                  checked={selectedView === 'Ranking'}
                  onChange={() => handleViewChange('Ranking')}
                  disabled={loading || loadingRankingReport}
                />
                <Radio
                  label="Geral"
                  name="goal-view"
                  value="general"
                  checked={selectedView === 'General'}
                  onChange={() => handleViewChange('General')}
                  disabled={loading || loadingRankingReport}
                />
              </S.RadioGroup>
              <S.MetaWrap>
                <S.MetaLabel design="hav-h7-text">Meta total</S.MetaLabel>
                {loading ? (
                  <Skeleton width={50} height={21} />
                ) : (
                  <S.MetaValue design="hav-h6-text">
                    {toDecimal(rankingTotalVolume)}
                  </S.MetaValue>
                )}
              </S.MetaWrap>
            </S.TitleRow>
          </S.SectionCard>
          {loading ? (
            <LoadingHavaianas />
          ) : (
            <>
              {selectedOffice !== 'TODOS' && selectedView === 'Ranking' && (
                <S.TabsWrapper>
                  <ActionTabs
                    items={getTabItems()}
                    currentItem={currentTab}
                    disabled={loading || loadingRankingReport}
                  />
                </S.TabsWrapper>
              )}
              {selectedView === 'Ranking' && (
                <RankingTable
                  quota={quota}
                  selectedOffice={selectedOffice}
                  selectedGroup={selectedGroup}
                  screenshotComponentRef={screenshotComponentRef}
                />
              )}
              {selectedView === 'General' && (
                <GeneralTable
                  quota={quota}
                  selectedOffice={selectedOffice}
                  screenshotComponentRef={screenshotComponentRef}
                  isPrinting={isPrinting}
                />
              )}
            </>
          )}
        </S.Content>
      </Accordion>
    </S.Wrapper>
  )
}
